/**
 * Precomputed word-onset captions for MP3 beat openers — the second timing
 * source of the opener karaoke (the first is live Cartesia SSE timestamps,
 * which a baked MP3 can never have).
 *
 * Generate with mlx-whisper against the shipped clip, same as
 * src/components/welcome/splashCaptions.ts (identical shape).
 *
 * TWO key forms are accepted (A1 + the render's caption delivery):
 *   - clip NAME, no extension (e.g. "state_sleep", "onboard_fork_form_1"):
 *     the render session's clipCaptions.ts format, so its Record<clipName,
 *     CaptionLine[]> content pastes in UNCHANGED. Lookup derives the name
 *     from the src basename, so /voice/ob/state_sleep.wav finds "state_sleep".
 *   - full clip src (e.g. "/voice/onboarding/<clip>.mp3"): the original form,
 *     still honored first for exact-path entries.
 *
 * A clip with no entry keeps today's duration-fraction reveal — adding
 * captions here upgrades it to word-accurate with zero code change.
 */
import type { CaptionLine } from '@/lib/voice/openerWordTimeline';
import { onsetsFromCaptions } from '@/lib/voice/openerWordTimeline';

const OPENER_CAPTIONS: Record<string, CaptionLine[]> = {
  // GENERATED by scripts/flow-sync/ingest-captions.ts from the render branch
  // clipCaptions.ts at de67b298 (mlx-whisper word timestamps). Re-run the
  // ingest when a clip is re-recorded; do not hand-edit the entries. The greeting
  // stays in splashCaptions.ts (component-owned SplashIntro).
  close: [
    {
      start: 0,
      end: 3.34,
      words: [
        { t: 0, w: 'Those' },
        { t: 0.32, w: 'are' },
        { t: 0.48, w: 'all' },
        { t: 0.68, w: 'in,' },
        { t: 1.36, w: 'and' },
        { t: 1.5, w: 'I' },
        { t: 1.6, w: 'marked' },
        { t: 1.88, w: 'each' },
        { t: 2.26, w: 'as' },
        { t: 2.5, w: 'build' },
        { t: 2.78, w: 'or' },
        { t: 3.04, w: 'break.' },
      ],
    },
    {
      start: 3.88,
      end: 5.02,
      words: [
        { t: 3.88, w: 'Tell' },
        { t: 4.04, w: 'me' },
        { t: 4.18, w: 'if' },
        { t: 4.32, w: 'any' },
        { t: 4.48, w: 'look' },
        { t: 4.7, w: 'wrong.' },
      ],
    },
    {
      start: 5.44,
      end: 7.44,
      words: [
        { t: 5.44, w: 'If' },
        { t: 5.62, w: "they're" },
        { t: 5.82, w: 'good,' },
        { t: 6.4, w: "we'll" },
        { t: 6.64, w: 'set' },
        { t: 6.82, w: 'the' },
        { t: 6.94, w: 'days' },
        { t: 7.16, w: 'next.' },
      ],
    },
  ],
  create_your_own: [
    {
      start: 0,
      end: 1.42,
      words: [
        { t: 0, w: 'Or' },
        { t: 0.34, w: 'you' },
        { t: 0.5, w: 'can' },
        { t: 0.66, w: 'create' },
        { t: 0.98, w: 'your' },
        { t: 1.22, w: 'own.' },
      ],
    },
  ],
  fork_question: [
    {
      start: 0,
      end: 3.26,
      words: [
        { t: 0, w: 'Do' },
        { t: 0.42, w: 'you' },
        { t: 0.58, w: 'already' },
        { t: 0.94, w: 'track' },
        { t: 1.42, w: 'habits' },
        { t: 1.9, w: 'or' },
        { t: 2.36, w: 'is' },
        { t: 2.48, w: 'this' },
        { t: 2.64, w: 'new' },
        { t: 2.92, w: 'to' },
        { t: 3.04, w: 'you?' },
      ],
    },
  ],
  mic_permission_1: [
    {
      start: 0,
      end: 1.68,
      words: [
        { t: 0, w: "I'd" },
        { t: 0.3, w: 'love' },
        { t: 0.48, w: 'to' },
        { t: 0.66, w: 'actually' },
        { t: 0.94, w: 'talk' },
        { t: 1.22, w: 'with' },
        { t: 1.46, w: 'you.' },
      ],
    },
    {
      start: 2.38,
      end: 4.76,
      words: [
        { t: 2.38, w: 'If' },
        { t: 2.62, w: 'you' },
        { t: 2.72, w: 'let' },
        { t: 2.9, w: 'me' },
        { t: 3.04, w: 'use' },
        { t: 3.2, w: 'your' },
        { t: 3.34, w: 'mic,' },
        { t: 3.76, w: 'you' },
        { t: 4.18, w: 'can' },
        { t: 4.32, w: 'just' },
        { t: 4.48, w: 'speak.' },
      ],
    },
  ],
  onboard_01_form_1: [
    {
      start: 0,
      end: 0.94,
      words: [
        { t: 0, w: 'How' },
        { t: 0.34, w: 'old' },
        { t: 0.6, w: 'are' },
        { t: 0.76, w: 'you?' },
      ],
    },
  ],
  onboard_01_form_2: [
    {
      start: 0,
      end: 0.86,
      words: [
        { t: 0, w: "What's" },
        { t: 0.46, w: 'your' },
        { t: 0.54, w: 'gender?' },
      ],
    },
  ],
  onboard_advanced_1: [
    {
      start: 0,
      end: 2.74,
      words: [
        { t: 0, w: 'Read' },
        { t: 0.4, w: 'me' },
        { t: 0.6, w: 'the' },
        { t: 0.76, w: 'list' },
        { t: 1.02, w: 'of' },
        { t: 1.14, w: 'the' },
        { t: 1.26, w: 'habits' },
        { t: 1.6, w: 'that' },
        { t: 1.86, w: 'you' },
        { t: 1.98, w: 'already' },
        { t: 2.24, w: 'track.' },
      ],
    },
    {
      start: 3.32,
      end: 5.44,
      words: [
        { t: 3.32, w: 'In' },
        { t: 3.46, w: 'the' },
        { t: 3.56, w: 'next' },
        { t: 3.82, w: 'step' },
        { t: 4.18, w: "we'll" },
        { t: 4.32, w: 'talk' },
        { t: 4.58, w: 'about' },
        { t: 4.78, w: 'which' },
        { t: 5.04, w: 'days.' },
      ],
    },
    {
      start: 6.1,
      end: 8.02,
      words: [
        { t: 6.1, w: 'For' },
        { t: 6.2, w: 'now' },
        { t: 6.58, w: 'just' },
        { t: 6.64, w: 'give' },
        { t: 6.84, w: 'me' },
        { t: 7, w: 'the' },
        { t: 7.08, w: 'list' },
        { t: 7.32, w: 'of' },
        { t: 7.46, w: 'your' },
        { t: 7.56, w: 'habits.' },
      ],
    },
    {
      start: 8.6,
      end: 9.98,
      words: [
        { t: 8.6, w: 'I' },
        { t: 8.66, w: 'recommend' },
        { t: 9.02, w: 'to' },
        { t: 9.34, w: 'start' },
        { t: 9.6, w: 'small.' },
      ],
    },
    {
      start: 10.52,
      end: 11.76,
      words: [
        { t: 10.52, w: 'You' },
        { t: 10.62, w: 'could' },
        { t: 10.78, w: 'always' },
        { t: 11.12, w: 'build' },
        { t: 11.4, w: 'on' },
        { t: 11.64, w: 'it.' },
      ],
    },
  ],
  onboard_advanced_frequency_1: [
    {
      start: 0,
      end: 2.52,
      words: [
        { t: 0, w: 'Not' },
        { t: 0.36, w: 'every' },
        { t: 0.62, w: 'habit' },
        { t: 0.94, w: 'needs' },
        { t: 1.26, w: 'to' },
        { t: 1.3, w: 'be' },
        { t: 1.42, w: 'done' },
        { t: 1.7, w: 'every' },
        { t: 2.04, w: 'day.' },
      ],
    },
    {
      start: 3.04,
      end: 5.22,
      words: [
        { t: 3.04, w: 'Specific' },
        { t: 3.52, w: 'days' },
        { t: 3.8, w: 'in' },
        { t: 3.98, w: 'the' },
        { t: 4.06, w: 'week' },
        { t: 4.26, w: 'is' },
        { t: 4.5, w: 'great' },
        { t: 4.78, w: 'as' },
        { t: 4.92, w: 'well.' },
      ],
    },
    {
      start: 5.78,
      end: 8.18,
      words: [
        { t: 5.78, w: 'Once' },
        { t: 6, w: 'a' },
        { t: 6.16, w: 'week' },
        { t: 6.38, w: 'for' },
        { t: 6.56, w: 'a' },
        { t: 6.66, w: 'certain' },
        { t: 6.86, w: 'habit,' },
        { t: 7.52, w: 'also' },
        { t: 7.84, w: 'great.' },
      ],
    },
  ],
  onboard_advanced_frequency_2: [
    {
      start: 0,
      end: 2.74,
      words: [
        { t: 0, w: 'Your' },
        { t: 0.32, w: 'habits' },
        { t: 0.72, w: 'are' },
        { t: 0.9, w: 'all' },
        { t: 1.1, w: 'set,' },
        { t: 1.86, w: 'your' },
        { t: 1.92, w: 'plan' },
        { t: 2.3, w: 'is' },
        { t: 2.5, w: 'ready.' },
      ],
    },
  ],
  onboard_beginner_01_1: [
    {
      start: 0,
      end: 3.78,
      words: [
        { t: 0, w: "Let's" },
        { t: 0.5, w: 'choose' },
        { t: 0.82, w: 'one' },
        { t: 1.2, w: 'area' },
        { t: 1.56, w: 'of' },
        { t: 1.74, w: 'your' },
        { t: 1.86, w: 'life' },
        { t: 2.26, w: 'that' },
        { t: 2.42, w: "you'd" },
        { t: 2.66, w: 'like' },
        { t: 2.84, w: 'to' },
        { t: 3.08, w: 'improve' },
        { t: 3.38, w: 'on.' },
      ],
    },
    {
      start: 4.28,
      end: 6.16,
      words: [
        { t: 4.28, w: 'Here' },
        { t: 4.5, w: 'are' },
        { t: 4.9, w: 'our' },
        { t: 4.98, w: 'recommended' },
        { t: 5.58, w: 'categories.' },
      ],
    },
  ],
  onboard_beginner_02_1: [
    {
      start: 0,
      end: 3.34,
      words: [
        { t: 0, w: 'So' },
        { t: 0.42, w: 'within' },
        { t: 0.68, w: 'that,' },
        { t: 1.46, w: 'which' },
        { t: 1.82, w: 'goals' },
        { t: 2.14, w: 'would' },
        { t: 2.32, w: 'you' },
        { t: 2.4, w: 'like' },
        { t: 2.58, w: 'to' },
        { t: 2.72, w: 'start' },
        { t: 2.94, w: 'with?' },
      ],
    },
    {
      start: 3.64,
      end: 5,
      words: [
        { t: 3.64, w: 'Pick' },
        { t: 3.96, w: 'one' },
        { t: 4.42, w: 'or' },
        { t: 4.72, w: 'two.' },
      ],
    },
  ],
  onboard_beginner_02_custom_1: [
    {
      start: 0,
      end: 2.78,
      words: [
        { t: 0, w: 'Tell' },
        { t: 0.32, w: 'me' },
        { t: 0.5, w: 'the' },
        { t: 0.62, w: 'goal' },
        { t: 0.88, w: 'you' },
        { t: 1.04, w: 'want' },
        { t: 1.2, w: 'to' },
        { t: 1.3, w: 'add,' },
        { t: 1.7, w: 'and' },
        { t: 2.1, w: "I'll" },
        { t: 2.24, w: 'set' },
        { t: 2.4, w: 'it' },
        { t: 2.52, w: 'up.' },
      ],
    },
  ],
  onboard_beginner_03_1: [
    {
      start: 0,
      end: 2.4,
      words: [
        { t: 0, w: 'Pick' },
        { t: 0.34, w: 'one' },
        { t: 0.64, w: 'or' },
        { t: 0.72, w: 'two' },
        { t: 1.06, w: 'habits' },
        { t: 1.52, w: 'that' },
        { t: 1.76, w: 'feel' },
        { t: 1.96, w: 'doable.' },
      ],
    },
    {
      start: 3.04,
      end: 7.6,
      words: [
        { t: 3.04, w: 'One' },
        { t: 3.18, w: 'habit' },
        { t: 3.58, w: 'that' },
        { t: 3.74, w: 'you' },
        { t: 3.84, w: 'actually' },
        { t: 4.18, w: 'keep' },
        { t: 4.64, w: 'is' },
        { t: 5.14, w: 'much' },
        { t: 5.44, w: 'better' },
        { t: 5.78, w: 'than' },
        { t: 6, w: 'a' },
        { t: 6.04, w: 'list' },
        { t: 6.26, w: 'of' },
        { t: 6.42, w: 'five' },
        { t: 6.74, w: 'that' },
        { t: 6.92, w: 'you' },
        { t: 7.02, w: "don't" },
        { t: 7.3, w: 'keep.' },
      ],
    },
    {
      start: 8.16,
      end: 10.16,
      words: [
        { t: 8.16, w: 'Create' },
        { t: 8.4, w: 'your' },
        { t: 8.62, w: 'own' },
        { t: 9.04, w: 'if' },
        { t: 9.28, w: 'nothing' },
        { t: 9.6, w: 'here' },
        { t: 9.86, w: 'fits.' },
      ],
    },
  ],
  onboard_beginner_03_custom_1: [
    {
      start: 0,
      end: 2.88,
      words: [
        { t: 0, w: 'Tell' },
        { t: 0.3, w: 'me' },
        { t: 0.46, w: 'the' },
        { t: 0.62, w: 'habit' },
        { t: 0.88, w: 'you' },
        { t: 1.08, w: 'want' },
        { t: 1.28, w: 'to' },
        { t: 1.42, w: 'add,' },
        { t: 1.84, w: 'and' },
        { t: 2.22, w: "I'll" },
        { t: 2.32, w: 'set' },
        { t: 2.54, w: 'it' },
        { t: 2.66, w: 'up.' },
      ],
    },
  ],
  onboard_beginner_04_1: [
    {
      start: 0,
      end: 3.16,
      words: [
        { t: 0, w: 'Please' },
        { t: 0.54, w: 'set' },
        { t: 0.74, w: 'the' },
        { t: 0.88, w: 'days' },
        { t: 1.26, w: 'that' },
        { t: 1.52, w: "you're" },
        { t: 1.58, w: 'going' },
        { t: 1.72, w: 'to' },
        { t: 1.92, w: 'actually' },
        { t: 2.32, w: 'do' },
        { t: 2.58, w: 'these' },
        { t: 2.8, w: 'habits.' },
      ],
    },
  ],
  onboard_beginner_04_2: [
    {
      start: 0,
      end: 2.5,
      words: [
        { t: 0, w: 'Not' },
        { t: 0.48, w: 'every' },
        { t: 0.82, w: 'habit' },
        { t: 1.12, w: 'needs' },
        { t: 1.46, w: 'to' },
        { t: 1.56, w: 'be' },
        { t: 1.64, w: 'done' },
        { t: 1.86, w: 'every' },
        { t: 2.08, w: 'day.' },
      ],
    },
    {
      start: 3,
      end: 6,
      words: [
        { t: 3, w: 'Three' },
        { t: 3.4, w: 'specific' },
        { t: 3.94, w: 'days' },
        { t: 4.34, w: 'in' },
        { t: 4.52, w: 'the' },
        { t: 4.62, w: 'week' },
        { t: 4.86, w: 'is' },
        { t: 5.16, w: 'great' },
        { t: 5.46, w: 'as' },
        { t: 5.7, w: 'well.' },
      ],
    },
    {
      start: 6.52,
      end: 9.38,
      words: [
        { t: 6.52, w: 'Once' },
        { t: 6.76, w: 'a' },
        { t: 6.94, w: 'week' },
        { t: 7.16, w: 'for' },
        { t: 7.36, w: 'specific' },
        { t: 7.84, w: 'habits,' },
        { t: 8.54, w: 'also' },
        { t: 9.06, w: 'great.' },
      ],
    },
  ],
  onboard_beginner_07_1: [
    {
      start: 0,
      end: 5.14,
      words: [
        { t: 0, w: 'One' },
        { t: 0.42, w: 'more.' },
        { t: 1.4, w: 'An' },
        { t: 1.54, w: 'evening' },
        { t: 1.92, w: 'reflection,' },
        { t: 3.26, w: 'a' },
        { t: 3.26, w: 'couple' },
        { t: 3.48, w: 'of' },
        { t: 3.68, w: 'minutes' },
        { t: 4, w: 'to' },
        { t: 4.22, w: 'close' },
        { t: 4.5, w: 'out' },
        { t: 4.72, w: 'your' },
        { t: 4.86, w: 'day.' },
      ],
    },
    {
      start: 5.78,
      end: 7.26,
      words: [
        { t: 5.78, w: 'Use' },
        { t: 6.16, w: 'these' },
        { t: 6.48, w: 'three' },
        { t: 6.72, w: 'questions.' },
      ],
    },
  ],
  onboard_complete_1: [
    {
      start: 0,
      end: 5.1,
      words: [
        { t: 0, w: "Here's" },
        { t: 0.66, w: 'your' },
        { t: 0.8, w: 'plan,' },
        { t: 1.68, w: 'your' },
        { t: 1.72, w: 'check' },
        { t: 2.08, w: 'in,' },
        { t: 2.72, w: 'your' },
        { t: 2.8, w: 'reflection' },
        { t: 3.28, w: 'and' },
        { t: 4, w: 'the' },
        { t: 4.16, w: 'habits' },
        { t: 4.56, w: 'you' },
        { t: 4.68, w: 'picked.' },
      ],
    },
    {
      start: 5.5,
      end: 8.46,
      words: [
        { t: 5.5, w: 'Want' },
        { t: 5.82, w: 'to' },
        { t: 6, w: 'start' },
        { t: 6.22, w: 'here' },
        { t: 6.64, w: 'or' },
        { t: 7.22, w: 'change' },
        { t: 7.58, w: 'anything' },
        { t: 8.02, w: 'first?' },
      ],
    },
  ],
  onboard_fork_form_1: [
    {
      start: 0,
      end: 2.72,
      words: [
        { t: 0, w: 'For' },
        { t: 0.3, w: 'the' },
        { t: 0.4, w: 'next' },
        { t: 0.6, w: 'part' },
        { t: 0.82, w: 'of' },
        { t: 0.96, w: 'the' },
        { t: 0.96, w: 'process,' },
        { t: 1.72, w: "I'd" },
        { t: 2.18, w: 'like' },
        { t: 2.38, w: 'to' },
        { t: 2.56, w: 'know:' },
      ],
    },
  ],
  onboard_morning_setup_1: [
    {
      start: 0,
      end: 4.32,
      words: [
        { t: 0, w: 'Part' },
        { t: 0.4, w: 'of' },
        { t: 0.56, w: 'the' },
        { t: 0.6, w: 'coaching' },
        { t: 0.9, w: 'process' },
        { t: 1.4, w: 'is' },
        { t: 1.8, w: 'doing' },
        { t: 2.08, w: 'this' },
        { t: 2.32, w: 'each' },
        { t: 2.62, w: 'day.' },
        { t: 3.34, w: 'It' },
        { t: 3.46, w: 'gives' },
        { t: 3.6, w: 'us' },
        { t: 3.8, w: 'two' },
        { t: 3.98, w: 'things.' },
      ],
    },
    {
      start: 4.94,
      end: 11.78,
      words: [
        { t: 4.94, w: 'First,' },
        { t: 5.7, w: "it's" },
        { t: 6, w: 'a' },
        { t: 6.06, w: 'real' },
        { t: 6.24, w: 'quick' },
        { t: 6.5, w: 'check' },
        { t: 6.78, w: '-in' },
        { t: 7, w: 'on' },
        { t: 7.24, w: 'how' },
        { t: 7.44, w: 'your' },
        { t: 7.6, w: 'state' },
        { t: 7.88, w: 'is,' },
        { t: 8.76, w: 'which' },
        { t: 8.92, w: 'is' },
        { t: 9.06, w: 'valuable,' },
        { t: 10.06, w: 'and' },
        { t: 10.34, w: 'people' },
        { t: 10.5, w: "don't" },
        { t: 10.82, w: 'usually' },
        { t: 11.1, w: 'do' },
        { t: 11.34, w: 'it' },
        { t: 11.44, w: 'enough.' },
      ],
    },
    {
      start: 12.48,
      end: 17.2,
      words: [
        { t: 12.48, w: 'In' },
        { t: 12.56, w: 'second,' },
        { t: 13.68, w: 'over' },
        { t: 13.74, w: 'time,' },
        { t: 14.3, w: 'it' },
        { t: 14.46, w: 'lets' },
        { t: 14.66, w: 'us' },
        { t: 14.8, w: 'see' },
        { t: 14.94, w: 'the' },
        { t: 15.06, w: 'connection' },
        { t: 15.44, w: 'between' },
        { t: 15.8, w: 'your' },
        { t: 16.02, w: 'behavior' },
        { t: 16.34, w: 'and' },
        { t: 16.72, w: 'your' },
        { t: 16.84, w: 'state.' },
      ],
    },
    {
      start: 17.82,
      end: 22.98,
      words: [
        { t: 17.82, w: 'So' },
        { t: 17.98, w: 'when' },
        { t: 18.22, w: 'would' },
        { t: 18.38, w: 'you' },
        { t: 18.46, w: 'like' },
        { t: 18.66, w: 'to' },
        { t: 18.8, w: 'do' },
        { t: 18.92, w: 'this' },
        { t: 19.08, w: 'each' },
        { t: 19.24, w: 'morning?' },
        { t: 20.22, w: 'I' },
        { t: 20.44, w: 'recommend' },
        { t: 20.88, w: '15' },
        { t: 21.6, w: 'minutes' },
        { t: 22.04, w: 'after' },
        { t: 22.5, w: 'you' },
        { t: 22.62, w: 'wake' },
        { t: 22.78, w: 'up.' },
      ],
    },
  ],
  onboard_morning_setup_2: [
    {
      start: 0,
      end: 1.64,
      words: [
        { t: 0, w: 'Every' },
        { t: 0.46, w: 'single' },
        { t: 0.84, w: 'day' },
        { t: 1.1, w: 'is' },
        { t: 1.26, w: 'great.' },
      ],
    },
    {
      start: 2.2,
      end: 6.6,
      words: [
        { t: 2.2, w: 'But' },
        { t: 2.48, w: 'doing' },
        { t: 2.68, w: 'weekdays' },
        { t: 3.2, w: 'consistently' },
        { t: 3.88, w: 'is' },
        { t: 4.3, w: 'better' },
        { t: 4.68, w: 'than' },
        { t: 5.04, w: 'every' },
        { t: 5.38, w: 'day' },
        { t: 5.82, w: 'in' },
        { t: 6, w: 'consistently.' },
      ],
    },
    {
      start: 7.32,
      end: 8.86,
      words: [
        { t: 7.32, w: 'So' },
        { t: 7.38, w: "that's" },
        { t: 7.6, w: 'what' },
        { t: 7.68, w: 'I' },
        { t: 7.8, w: 'recommend' },
        { t: 8.18, w: 'to' },
        { t: 8.52, w: 'start.' },
      ],
    },
    {
      start: 9.32,
      end: 11.36,
      words: [
        { t: 9.32, w: 'But' },
        { t: 9.6, w: "you're" },
        { t: 9.74, w: 'welcome' },
        { t: 10, w: 'to' },
        { t: 10.26, w: 'add' },
        { t: 10.46, w: 'the' },
        { t: 10.58, w: 'weekend' },
        { t: 10.9, w: 'as' },
        { t: 11.18, w: 'well.' },
      ],
    },
  ],
  onboard_state_check_1: [
    {
      start: 0,
      end: 4.84,
      words: [
        { t: 0, w: "I'd" },
        { t: 0.3, w: 'like' },
        { t: 0.48, w: 'to' },
        { t: 0.58, w: 'invite' },
        { t: 0.9, w: 'you' },
        { t: 1.18, w: 'into' },
        { t: 1.34, w: 'a' },
        { t: 1.48, w: 'coaching' },
        { t: 1.74, w: 'process' },
        { t: 2.24, w: 'together.' },
        { t: 3.06, w: 'And' },
        { t: 3.38, w: "it's" },
        { t: 3.6, w: 'built' },
        { t: 3.8, w: 'on' },
        { t: 4.06, w: 'a' },
        { t: 4.18, w: 'few' },
        { t: 4.36, w: 'components' },
      ],
    },
    {
      start: 4.84,
      end: 6.4,
      words: [
        { t: 4.84, w: "we'll" },
        { t: 5.12, w: 'go' },
        { t: 5.26, w: 'through' },
        { t: 5.58, w: 'on' },
        { t: 5.82, w: 'the' },
        { t: 5.92, w: 'way' },
        { t: 6.1, w: 'in.' },
      ],
    },
    {
      start: 6.66,
      end: 7.4,
      words: [
        { t: 6.66, w: "It's" },
        { t: 6.88, w: 'built' },
        { t: 7.04, w: 'light.' },
      ],
    },
    {
      start: 7.94,
      end: 11.4,
      words: [
        { t: 7.94, w: 'I' },
        { t: 8.04, w: 'believe' },
        { t: 8.38, w: 'less' },
        { t: 8.7, w: 'is' },
        { t: 8.88, w: 'more,' },
        { t: 9.7, w: 'especially' },
        { t: 10.16, w: 'in' },
        { t: 10.44, w: 'the' },
        { t: 10.48, w: 'beginning' },
        { t: 10.76, w: 'of' },
        { t: 10.98, w: 'a' },
        { t: 11.04, w: 'process.' },
      ],
    },
  ],
  onboard_state_check_2: [
    {
      start: 0,
      end: 5.5,
      words: [
        { t: 0, w: 'Whether' },
        { t: 0.44, w: "you've" },
        { t: 0.68, w: 'never' },
        { t: 0.88, w: 'done' },
        { t: 1.16, w: 'something' },
        { t: 1.48, w: 'like' },
        { t: 1.66, w: 'this' },
        { t: 1.82, w: 'before' },
        { t: 2.26, w: 'or' },
        { t: 2.64, w: 'you' },
        { t: 2.7, w: 'already' },
        { t: 3.06, w: 'track' },
        { t: 3.5, w: 'a' },
        { t: 3.68, w: 'lot,' },
        { t: 4.1, w: 'it' },
        { t: 4.38, w: 'is' },
        { t: 4.52, w: 'built' },
        { t: 4.86, w: 'for' },
        { t: 5.16, w: 'you.' },
      ],
    },
    {
      start: 5.9,
      end: 7.94,
      words: [
        { t: 5.9, w: "I'll" },
        { t: 6.1, w: 'explain' },
        { t: 6.62, w: 'each' },
        { t: 7.04, w: 'part' },
        { t: 7.34, w: 'as' },
        { t: 7.52, w: 'we' },
        { t: 7.66, w: 'go.' },
      ],
    },
    {
      start: 8.7,
      end: 9.72,
      words: [
        { t: 8.7, w: 'This' },
        { t: 8.86, w: 'is' },
        { t: 9, w: 'the' },
        { t: 9.08, w: 'first' },
        { t: 9.3, w: 'part.' },
      ],
    },
    {
      start: 10.08,
      end: 13.2,
      words: [
        { t: 10.08, w: 'A' },
        { t: 10.22, w: 'quick' },
        { t: 10.48, w: 'state' },
        { t: 10.78, w: 'check' },
        { t: 11.08, w: '-in,' },
        { t: 11.54, w: 'and' },
        { t: 11.8, w: "I'd" },
        { t: 11.98, w: 'like' },
        { t: 12.14, w: 'you' },
        { t: 12.26, w: 'to' },
        { t: 12.34, w: 'do' },
        { t: 12.5, w: 'it' },
        { t: 12.68, w: 'right' },
        { t: 12.86, w: 'now.' },
      ],
    },
  ],
  onboard_weekly_projection_blank_1: [
    {
      start: 0,
      end: 3.06,
      words: [
        { t: 0, w: 'This' },
        { t: 0.4, w: 'is' },
        { t: 0.56, w: 'your' },
        { t: 0.74, w: 'week.' },
        { t: 1.4, w: 'Blank,' },
        { t: 2.28, w: 'starting' },
        { t: 2.66, w: 'today.' },
      ],
    },
  ],
  onboard_weekly_projection_full_1: [
    {
      start: 0,
      end: 5.22,
      words: [
        { t: 0, w: 'Best' },
        { t: 0.44, w: 'case,' },
        { t: 1.22, w: 'every' },
        { t: 1.48, w: 'day' },
        { t: 1.76, w: 'green,' },
        { t: 2.5, w: '100' },
        { t: 2.92, w: '%' },
        { t: 3.36, w: 'success.' },
        { t: 4.36, w: 'That' },
        { t: 4.62, w: 'would' },
        { t: 4.76, w: 'be' },
        { t: 4.86, w: 'amazing.' },
      ],
    },
  ],
  onboard_weekly_projection_gaps_1: [
    {
      start: 0,
      end: 2.2,
      words: [
        { t: 0, w: 'The' },
        { t: 0.34, w: 'one' },
        { t: 0.54, w: 'thing' },
        { t: 0.74, w: 'you' },
        { t: 0.9, w: 'want' },
        { t: 1.06, w: 'to' },
        { t: 1.18, w: 'avoid' },
        { t: 1.5, w: 'is' },
        { t: 1.7, w: 'this.' },
      ],
    },
    {
      start: 2.6,
      end: 4.52,
      words: [
        { t: 2.6, w: 'The' },
        { t: 2.74, w: 'empty' },
        { t: 3.06, w: 'days' },
        { t: 3.44, w: 'you' },
        { t: 3.58, w: 'never' },
        { t: 3.94, w: 'reported.' },
      ],
    },
    {
      start: 5.12,
      end: 7.64,
      words: [
        { t: 5.12, w: 'Stay' },
        { t: 5.32, w: 'consistent,' },
        { t: 6.74, w: 'just' },
        { t: 6.96, w: 'report' },
        { t: 7.34, w: 'it.' },
      ],
    },
    {
      start: 8.24,
      end: 9.66,
      words: [
        { t: 8.24, w: 'Even' },
        { t: 8.44, w: 'a' },
        { t: 8.62, w: 'miscounts.' },
      ],
    },
    {
      start: 9.88,
      end: 11.3,
      words: [
        { t: 9.88, w: 'That' },
        { t: 10.14, w: 'keeps' },
        { t: 10.38, w: 'the' },
        { t: 10.54, w: 'momentum' },
        { t: 10.92, w: 'going.' },
      ],
    },
  ],
  onboard_weekly_projection_p36_1: [
    {
      start: 0,
      end: 1.78,
      words: [
        { t: 0, w: 'Some' },
        { t: 0.44, w: 'weeks' },
        { t: 0.7, w: 'can' },
        { t: 0.92, w: 'look' },
        { t: 1.08, w: 'like' },
        { t: 1.28, w: 'this.' },
      ],
    },
    {
      start: 2.24,
      end: 3.2,
      words: [
        { t: 2.24, w: 'And' },
        { t: 2.32, w: 'even' },
        { t: 2.5, w: "that's" },
        { t: 2.86, w: 'okay,' },
      ],
    },
    {
      start: 3.68,
      end: 6.98,
      words: [
        { t: 3.68, w: 'because' },
        { t: 3.96, w: "you're" },
        { t: 4.08, w: 'in' },
        { t: 4.24, w: 'the' },
        { t: 4.36, w: 'process' },
        { t: 4.74, w: 'and' },
        { t: 5.24, w: "you're" },
        { t: 5.42, w: 'consistent' },
        { t: 5.86, w: 'inside' },
        { t: 6.46, w: 'the' },
        { t: 6.64, w: 'process.' },
      ],
    },
  ],
  onboard_weekly_projection_p78_1: [
    {
      start: 0,
      end: 6.66,
      words: [
        { t: 0, w: 'Most' },
        { t: 0.44, w: 'likely' },
        { t: 0.8, w: 'your' },
        { t: 1, w: 'week' },
        { t: 1.24, w: 'looks' },
        { t: 1.52, w: 'somewhere' },
        { t: 1.8, w: 'around' },
        { t: 2.16, w: 'here.' },
        { t: 2.88, w: 'Mostly' },
        { t: 3.16, w: 'green,' },
        { t: 4.2, w: 'a' },
        { t: 4.26, w: 'few' },
        { t: 4.42, w: 'misses.' },
        { t: 5.52, w: 'Still' },
        { t: 5.84, w: 'a' },
        { t: 6.16, w: 'real' },
        { t: 6.38, w: 'win.' },
      ],
    },
  ],
  reflect_alt: [
    {
      start: 0,
      end: 2.38,
      words: [
        { t: 0, w: 'Or' },
        { t: 0.32, w: 'make' },
        { t: 0.52, w: 'your' },
        { t: 0.66, w: 'own,' },
        { t: 1.48, w: 'or' },
        { t: 1.58, w: 'just' },
        { t: 1.78, w: 'talk' },
        { t: 2.06, w: 'freely.' },
      ],
    },
  ],
  reflect_forgive: [
    {
      start: 0,
      end: 1.8,
      words: [
        { t: 0, w: 'What' },
        { t: 0.42, w: 'do' },
        { t: 0.54, w: 'I' },
        { t: 0.7, w: 'forgive' },
        { t: 1.04, w: 'myself' },
        { t: 1.56, w: 'for?' },
      ],
    },
  ],
  reflect_grateful: [
    {
      start: 0,
      end: 1.3,
      words: [
        { t: 0, w: 'What' },
        { t: 0.34, w: 'am' },
        { t: 0.46, w: 'I' },
        { t: 0.6, w: 'grateful' },
        { t: 0.92, w: 'for?' },
      ],
    },
  ],
  reflect_proud: [
    {
      start: 0,
      end: 1.2,
      words: [
        { t: 0, w: 'What' },
        { t: 0.4, w: 'am' },
        { t: 0.54, w: 'I' },
        { t: 0.7, w: 'proud' },
        { t: 1.02, w: 'of?' },
      ],
    },
  ],
  reflect_time: [
    {
      start: 0,
      end: 2.42,
      words: [
        { t: 0, w: "I'd" },
        { t: 0.4, w: 'recommend' },
        { t: 0.9, w: 'doing' },
        { t: 1.48, w: 'this' },
        { t: 1.72, w: 'before' },
        { t: 1.98, w: 'bed,' },
      ],
    },
    {
      start: 2.88,
      end: 5.3,
      words: [
        { t: 2.88, w: 'maybe' },
        { t: 3.12, w: '15' },
        { t: 3.7, w: 'minutes' },
        { t: 4.16, w: 'before' },
        { t: 4.54, w: 'you' },
        { t: 4.68, w: 'wind' },
        { t: 5.02, w: 'down.' },
      ],
    },
  ],
  state_energy: [
    {
      start: 0,
      end: 0.92,
      words: [
        { t: 0, w: "How's" },
        { t: 0.4, w: 'your' },
        { t: 0.52, w: 'energy?' },
      ],
    },
  ],
  state_mood: [
    {
      start: 0,
      end: 0.72,
      words: [
        { t: 0, w: "How's" },
        { t: 0.42, w: 'your' },
        { t: 0.52, w: 'mood?' },
      ],
    },
  ],
  state_sleep: [
    {
      start: 0,
      end: 0.86,
      words: [
        { t: 0, w: "How's" },
        { t: 0.44, w: 'your' },
        { t: 0.62, w: 'sleep?' },
      ],
    },
  ],
  state_stress: [
    {
      start: 0,
      end: 0.88,
      words: [
        { t: 0, w: "How's" },
        { t: 0.44, w: 'your' },
        { t: 0.58, w: 'stress?' },
      ],
    },
  ],
};

/** "/voice/ob/state_sleep.wav" -> "state_sleep" (the clip-name key form). */
function clipNameFromSrc(src: string): string {
  const base = src.slice(src.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(0, dot) : base;
}

export function openerCaptionOnsets(src: string | null): number[] | null {
  if (!src) return null;
  const lines = OPENER_CAPTIONS[src] ?? OPENER_CAPTIONS[clipNameFromSrc(src)];
  if (!lines || lines.length === 0) return null;
  const onsets = onsetsFromCaptions(lines);
  return onsets.length > 0 ? onsets : null;
}
